import React, {useRef, useState, useEffect} from 'react';
import {v4 as uuidv4} from 'uuid';
import {useUrl} from '@shopify/hydrogen';
import {manualUrlParams} from '~/consts/urlParams';
import {Text} from '~/components';
import {ProductGridGallery} from '~/components/product/grid/ProductGridGallery.client';
import {getCookie, setCookie} from '~/utils/cookies';
import {
  SEARCHSPRING_SESSION_COOKIE,
  SEARCHSPRING_USER_COOKIE,
} from '~/consts/cookies';

export default function InfiniteScrollClientV2({
  handle,
  resultsPerPage = 2, // Default number of items per page
  displayType,
  endOfListText,
  language = 'EN',
  country = 'US',
  searchspringData,
  setSearchspringData,
  sort,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(
    searchspringData?.pagination?.currentPage ?? 0,
  );
  const [hasMore, setHasMore] = useState(true);
  const debounceTimerRef = useRef(null);
  const isInitialMount = useRef(true);
  const {searchParams} = useUrl();
  const [searchspringSessionId, setSearchspringSessionId] = useState(uuidv4());
  const [searchspringUserId, setSearchspringUserId] = useState(uuidv4());
  const [searchspringPageLoadId, setSearchspringPageLoadId] = useState();

  const windowReady = typeof window !== 'undefined';
  const q = searchParams.get(manualUrlParams.SEARCH_TERM) ?? ''; // Empty yields all products

  // Cookie persistence effects - manage shared subdomain cookies re: session
  useEffect(() => {
    if (!windowReady) return;
    if (!searchspringPageLoadId) setSearchspringPageLoadId(uuidv4());

    const existingSessionId = getCookie(SEARCHSPRING_SESSION_COOKIE);
    if (existingSessionId?.length) {
      setSearchspringSessionId(existingSessionId);
    } else {
      setCookie(SEARCHSPRING_SESSION_COOKIE, searchspringSessionId, 1);
    }

    const existingUserId = getCookie(SEARCHSPRING_USER_COOKIE);
    if (existingUserId?.length) {
      setSearchspringUserId(existingUserId);
    } else {
      setCookie(SEARCHSPRING_USER_COOKIE, searchspringUserId, 1);
    }
  }, [windowReady]);

  useEffect(() => {
    if (
      searchspringSessionId?.length &&
      searchspringUserId &&
      searchspringPageLoadId?.length
    ) {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        if (!searchspringData) {
          fetchData();
        }
      }
    }
  }, [searchspringSessionId, searchspringPageLoadId]);
  useEffect(() => {
    if (!isInitialMount.current) {
      setHasMore(true);
      setSearchspringData(null);
      setCurrentPage(0);
      fetchData(true, 0);
    }
  }, [sort]);
  useEffect(() => {
    if (
      searchspringSessionId?.length &&
      searchspringUserId &&
      searchspringPageLoadId?.length
    ) {
      const handleScroll = () => {
        const footer = document.querySelector('.footer');
        const footerHeight = footer ? footer.getBoundingClientRect().height : 0;
        const bottomOffset = footerHeight + 100;

        if (
          window.innerHeight + window.scrollY >=
            document.body.offsetHeight - bottomOffset &&
          !isLoading &&
          hasMore
        ) {
          if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = setTimeout(() => {
            fetchData();
          }, 200);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      };
    }
  }, [
    isLoading,
    hasMore,
    searchspringSessionId,
    setSearchspringUserId,
    searchspringPageLoadId,
  ]);

  const fetchData = async (force = false, page = currentPage) => {
    if (!force) {
      if (!hasMore || isLoading) return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const nextPage = page + 1;

      const baseUrl = `/api/searchspring\
?language=${encodeURIComponent(language)}\
&country=${encodeURIComponent(country)}\
&resultsFormat=json\
&resultsPerPage=${encodeURIComponent(resultsPerPage)}\
&page=${encodeURIComponent(nextPage)}\
&${sort}\
&redirectResponse=minimal`;
      let queryUrl = baseUrl;
      if (q) {
        queryUrl += `&q=${encodeURIComponent(q)}`;
      }
      if (handle) {
        queryUrl += `&excludedFacets=collection_name&excludedFacets=collection_handle&bgfilter.collection_handle=${encodeURIComponent(
          handle,
        )}`;
      }

      // Generate filters based on search parameters
      let filters = '';
      searchParams.forEach((value, key) => {
        if (key.startsWith('filter.')) {
          filters += `&${key}=${encodeURIComponent(value)}`;
        }
      });
      queryUrl += filters;

      const options = {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'searchspring-session-id': searchspringSessionId,
          'searchspring-user-id': searchspringUserId,
          'searchspring-page-load-id': searchspringPageLoadId,
        },
      };
      const response = await fetch(queryUrl, options);
      if (!response.ok) throw new Error('Failed to fetch data');

      const newData = await response.json();

      setCurrentPage(newData.pagination.currentPage);
      setHasMore(
        newData.pagination.currentPage < newData.pagination.totalPages,
      );
      setSearchspringData((prevData) => ({
        ...newData,
        results: [...(prevData?.results || []), ...(newData?.results || [])],
        sanityProducts: [
          ...(prevData?.sanityProducts || []),
          ...(newData?.sanityProducts || []),
        ],
        shopifyProducts: {
          ...newData.shopifyProducts,
          nodes: [
            ...(prevData?.shopifyProducts?.nodes || []),
            ...(newData?.shopifyProducts?.nodes || []),
          ],
        },
        variantOverrides: [
          ...(prevData?.variantOverrides || []),
          ...(newData?.variantOverrides || []),
        ],
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="infinite-scroll-wrapper">
      {searchspringData?.results?.length > 0 && (
        <ProductGridGallery
          sanityProducts={searchspringData?.sanityProducts}
          shopifyProducts={searchspringData?.shopifyProducts ?? {}}
          btnCtaText="Shop Now"
          cardsPerRow={parseInt(displayType ?? '3', 10)}
          intellisuggestData={searchspringData?.intellisuggestData}
          intellisuggestSignature={searchspringData?.intellisuggestSignature}
          variantOverrides={searchspringData?.variantOverrides}
        />
      )}

      {isLoading && (
        <div style={{maxWidth: '1600px', margin: 'auto', textAlign: 'center'}}>
          <Text format as="p" className="infinite-scroll-text">
            Loading...
          </Text>
        </div>
      )}

      {!hasMore && searchspringData.results.length > 0 && (
        <div style={{maxWidth: '1600px', margin: 'auto', textAlign: 'center'}}>
          <Text format as="p" className="infinite-scroll-text fade-out">
            {endOfListText ?? "You've reached the end of the list."}
          </Text>
        </div>
      )}

      {!isLoading && !hasMore && searchspringData.results.length === 0 && (
        <div style={{maxWidth: '1600px', margin: 'auto', textAlign: 'center'}}>
          <Text format as="p" className="infinite-scroll-text fade-out">
            No products found.
          </Text>
        </div>
      )}

      {error && (
        <div style={{maxWidth: '1600px', margin: 'auto', padding: '2rem'}}>
          <Text format as="p">
            Error: {error.message}
          </Text>
        </div>
      )}
    </div>
  );
}
